import { useState, useEffect } from "react";
import { 
  GripVertical, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  PoundSterling,
  Calendar,
  MoreVertical,
  Eye,
  Flame,
  Snowflake,
  Thermometer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { id: "new_lead", label: "New Lead", color: "#3B82F6", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
  { id: "qualified", label: "Qualified", color: "#8B5CF6", bgColor: "bg-purple-50", borderColor: "border-purple-200" },
  { id: "site_visit_scheduled", label: "Site Visit", color: "#F59E0B", bgColor: "bg-amber-50", borderColor: "border-amber-200" },
  { id: "quote_sent", label: "Quote Sent", color: "#6366F1", bgColor: "bg-indigo-50", borderColor: "border-indigo-200" },
  { id: "job_won", label: "Job Won", color: "#10B981", bgColor: "bg-green-50", borderColor: "border-green-200" },
  { id: "job_lost", label: "Job Lost", color: "#6B7280", bgColor: "bg-gray-50", borderColor: "border-gray-200" },
  { id: "job_completed", label: "Completed", color: "#059669", bgColor: "bg-emerald-50", borderColor: "border-emerald-200" }
];

const LeadCard = ({ lead, onDragStart, onViewLead }) => {
  const temperatureColors = {
    hot: { bg: "bg-red-100", text: "text-red-600", icon: Flame },
    warm: { bg: "bg-orange-100", text: "text-orange-600", icon: Thermometer },
    cold: { bg: "bg-blue-100", text: "text-blue-600", icon: Snowflake }
  };
  
  const tempInfo = lead.temperature ? temperatureColors[lead.temperature] : null;
  const TempIcon = tempInfo?.icon;
  
  const handleCardClick = (e) => {
    // Don't trigger click when dragging
    if (e.defaultPrevented) return;
    onViewLead(lead);
  };
  
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, lead)}
      onClick={handleCardClick}
      className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-pointer group"
      data-testid={`kanban-card-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-gray-300 group-hover:text-gray-500 cursor-grab" />
          <h4 className="font-medium text-gray-900 text-sm truncate max-w-[140px]">
            {lead.customer_name || lead.name || "Unknown"}
          </h4>
        </div>
        <div className="flex items-center gap-1">
          {tempInfo && (
            <span className={`p-1 rounded ${tempInfo.bg}`}>
              <TempIcon className={`w-3 h-3 ${tempInfo.text}`} />
            </span>
          )}
        </div>
      </div>
      
      <p className="text-xs text-gray-500 mb-2 truncate">
        {lead.project_type || lead.quote_type || "General Enquiry"}
      </p>
      
      <div className="space-y-1 text-xs text-gray-600">
        {lead.phone && (
          <div className="flex items-center gap-1">
            <Phone className="w-3 h-3" />
            <span className="truncate">{lead.phone}</span>
          </div>
        )}
        {lead.postcode && (
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>{lead.postcode}</span>
          </div>
        )}
      </div>
      
      {(lead.job_value_estimate || lead.estimate_high) && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Est. Value</span>
            <span className="text-sm font-semibold text-primary">
              £{(lead.job_value_estimate || lead.estimate_high || 0).toLocaleString()}
            </span>
          </div>
        </div>
      )}
      
      {lead.lead_source && (
        <div className="mt-1">
          <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
            {lead.lead_source}
          </span>
        </div>
      )}
    </div>
  );
};

const KanbanColumn = ({ stage, leads, onDrop, onDragOver, onDragStart, onViewLead, totalValue }) => {
  const stageConfig = PIPELINE_STAGES.find(s => s.id === stage.id) || stage;
  
  return (
    <div 
      className={`flex-shrink-0 w-72 ${stageConfig.bgColor} rounded-xl border ${stageConfig.borderColor}`}
      onDrop={(e) => onDrop(e, stage.id)}
      onDragOver={onDragOver}
      data-testid={`kanban-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: stageConfig.color }}
            />
            <h3 className="font-semibold text-gray-900 text-sm">{stageConfig.label}</h3>
          </div>
          <span className="text-xs font-medium px-2 py-0.5 bg-white rounded-full text-gray-600">
            {leads.length}
          </span>
        </div>
        {totalValue > 0 && (
          <p className="text-xs text-gray-500">
            £{totalValue.toLocaleString()} pipeline
          </p>
        )}
      </div>
      
      {/* Cards */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto">
        {leads.map((lead) => (
          <LeadCard 
            key={lead.id} 
            lead={lead} 
            onDragStart={onDragStart}
            onViewLead={onViewLead}
          />
        ))}
        {leads.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            <p>No leads</p>
            <p className="text-xs mt-1">Drag leads here</p>
          </div>
        )}
      </div>
    </div>
  );
};

const KanbanBoard = ({ token, onViewLead }) => {
  const [pipeline, setPipeline] = useState({});
  const [stages, setStages] = useState(PIPELINE_STAGES);
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/owner/crm/pipeline`, { headers });
      setPipeline(response.data.pipeline || {});
      if (response.data.stages) {
        setStages(response.data.stages);
      }
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", lead.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    if (!draggedLead) return;
    
    const oldStage = draggedLead.pipeline_stage || draggedLead.status;
    if (oldStage === newStage) {
      setDraggedLead(null);
      return;
    }
    
    // Optimistic update
    const updatedPipeline = { ...pipeline };
    
    // Remove from old stage
    if (updatedPipeline[oldStage]) {
      updatedPipeline[oldStage] = updatedPipeline[oldStage].filter(l => l.id !== draggedLead.id);
    }
    
    // Add to new stage
    const updatedLead = { ...draggedLead, pipeline_stage: newStage, status: newStage };
    if (!updatedPipeline[newStage]) {
      updatedPipeline[newStage] = [];
    }
    updatedPipeline[newStage] = [updatedLead, ...updatedPipeline[newStage]];
    
    setPipeline(updatedPipeline);
    
    try {
      await axios.put(`${API}/owner/crm/move-stage?lead_id=${draggedLead.id}&new_stage=${newStage}`, {}, { headers });
      toast.success(`Moved to ${stages.find(s => s.id === newStage)?.label || newStage}`);
    } catch (error) {
      console.error("Error moving lead:", error);
      toast.error("Failed to move lead");
      // Revert on error
      fetchPipeline();
    } finally {
      setDraggedLead(null);
    }
  };

  const calculateStageValue = (stageId) => {
    const leads = pipeline[stageId] || [];
    return leads.reduce((sum, lead) => {
      return sum + (lead.job_value_estimate || lead.estimate_high || 0);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-4" data-testid="kanban-board">
      <div className="flex gap-4 min-w-max p-1">
        {stages.map((stage) => (
          <KanbanColumn
            key={stage.id}
            stage={stage}
            leads={pipeline[stage.id] || []}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
            onViewLead={onViewLead}
            totalValue={calculateStageValue(stage.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default KanbanBoard;
